import { useState } from 'react'
import { MdInfo, MdNotifications, MdNotificationsOff, MdWarning } from 'react-icons/md'
import { useOneSignal } from '@/lib/hooks/use-onesignal'
import {
    isFloatingNotificationsDisabled,
    setFloatingNotificationsDisabled,
} from '@/lib/hooks/use-notifications'
import { useAuth } from '@/store/hooks'
import SystemEmailsCard from './system-emails-card'

/**
 * Aba de notificações: permissão do push (OneSignal), o alerta flutuante e os
 * e-mails que a plataforma dispara. Autocontida — o estado de push é só dela.
 */
export default function NotificationsTab() {
    const { isCompany, isMaster } = useAuth()
    const {
        isInitialized: pushInitialized,
        isSubscribed: pushSubscribed,
        isLoading: pushLoading,
        requestPermission: pushRequestPermission,
        unsubscribe: pushUnsubscribe,
    } = useOneSignal()
    const [pushActioning, setPushActioning] = useState(false)
    const [floatingNotifDisabled, setFloatingNotifDisabled] = useState(
        isFloatingNotificationsDisabled()
    )

    return (
            <div className="flex flex-col gap-6">
                <h2 className="text-xl font-bold text-base-content">
                    Notificações Push
                </h2>

                <div className="alert alert-info">
                    <MdInfo className="text-xl shrink-0" />
                    <span>
                        As notificações push permitem que você
                        receba alertas em tempo real sobre novos
                        atendimentos, pedidos e outras
                        atividades importantes, mesmo quando o
                        navegador está em segundo plano.
                    </span>
                </div>

                <div className="card bg-base-200/50 border border-base-300">
                    <div className="card-body">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                {pushSubscribed ? (
                                    <div className="bg-success/20 p-3 rounded-full">
                                        <MdNotifications className="text-2xl text-success" />
                                    </div>
                                ) : (
                                    <div className="bg-base-300 p-3 rounded-full">
                                        <MdNotificationsOff className="text-2xl text-base-content/50" />
                                    </div>
                                )}
                                <div>
                                    <h3 className="font-semibold text-lg">
                                        {pushSubscribed
                                            ? 'Notificações ativadas'
                                            : 'Notificações desativadas'}
                                    </h3>
                                    <p className="text-sm text-base-content/60">
                                        {pushSubscribed
                                            ? 'Você está recebendo notificações push neste dispositivo.'
                                            : 'Ative para receber alertas em tempo real neste dispositivo.'}
                                    </p>
                                </div>
                            </div>

                            {pushLoading ? (
                                <span className="loading loading-spinner loading-md"></span>
                            ) : pushSubscribed ? (
                                <button
                                    className="btn btn-error btn-outline"
                                    onClick={async () => {
                                        setPushActioning(true)
                                        await pushUnsubscribe()
                                        setPushActioning(false)
                                    }}
                                    disabled={
                                        pushActioning ||
                                        !pushInitialized
                                    }
                                >
                                    {pushActioning ? (
                                        <span className="loading loading-spinner loading-sm"></span>
                                    ) : (
                                        <>
                                            <MdNotificationsOff className="text-lg" />
                                            Desativar
                                        </>
                                    )}
                                </button>
                            ) : (
                                <button
                                    className="btn btn-secondary"
                                    onClick={async () => {
                                        setPushActioning(true)
                                        await pushRequestPermission()
                                        setPushActioning(false)
                                    }}
                                    disabled={
                                        pushActioning ||
                                        !pushInitialized
                                    }
                                >
                                    {pushActioning ? (
                                        <span className="loading loading-spinner loading-sm"></span>
                                    ) : (
                                        <>
                                            <MdNotifications className="text-lg" />
                                            Ativar Notificações
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="card bg-base-200/50 border border-base-300">
                    <div className="card-body">
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div
                                    className={`p-3 rounded-full ${
                                        floatingNotifDisabled
                                            ? 'bg-base-300'
                                            : 'bg-secondary/20'
                                    }`}
                                >
                                    {floatingNotifDisabled ? (
                                        <MdNotificationsOff className="text-2xl text-base-content/50" />
                                    ) : (
                                        <MdNotifications className="text-2xl text-secondary" />
                                    )}
                                </div>
                                <div>
                                    <h3 className="font-semibold text-lg">
                                        Notificações flutuantes
                                    </h3>
                                    <p className="text-sm text-base-content/60">
                                        Mostra um aviso rápido no
                                        canto inferior a cada nova
                                        notificação. Desative para
                                        receber apenas no ícone de
                                        sino (neste dispositivo).
                                    </p>
                                </div>
                            </div>
                            <input
                                type="checkbox"
                                className="toggle toggle-secondary"
                                checked={!floatingNotifDisabled}
                                onChange={e => {
                                    const disabled =
                                        !e.target.checked
                                    setFloatingNotificationsDisabled(
                                        disabled
                                    )
                                    setFloatingNotifDisabled(
                                        disabled
                                    )
                                }}
                            />
                        </div>
                    </div>
                </div>

                {Notification.permission === 'denied' && (
                    <div className="alert alert-warning">
                        <MdWarning className="text-xl shrink-0" />
                        <div>
                            <h4 className="font-bold">
                                Notificações bloqueadas pelo
                                navegador
                            </h4>
                            <p className="text-sm">
                                Você bloqueou as notificações
                                para este site. Para reativar,
                                clique no cadeado na barra de
                                endereço do navegador e permita
                                notificações.
                            </p>
                        </div>
                    </div>
                )}

                <div className="divider"></div>

                <SystemEmailsCard canEdit={isMaster || isCompany} />
            </div>
    )
}
